import { useState } from "react";
import { Link } from "react-router-dom";
import AdminDashboardMenu from "../components/AdminDashboardMenu";
import MindGameBrand from "../components/MindGameBrand";
import { BRAND_ASSET_GROUPS, BRAND_COLORS } from "../config/brandAssets";
import "./brandLibrary.css";

const url = path => `${process.env.PUBLIC_URL}/${path}`;

function BrandAsset({ asset }) {
  const [failed, setFailed] = useState(false);
  const animated = asset.name.endsWith("-animated.svg");
  const [playing, setPlaying] = useState(false);
  const [revision, setRevision] = useState(0);
  const path = animated && !playing ? asset.path.replace("-animated.svg", ".svg") : asset.path;
  return <article className="brand-library-card">
    <div className="brand-library-art">
      {failed ? <p role="alert">Unable to load {asset.name}.</p> :
        <img key={`${path}-${revision}`} src={`${url(path)}${animated && playing ? `?replay=${revision}` : ""}`}
          alt={asset.name} loading="lazy" onError={() => {
            console.error("Brand library asset could not load:", asset.path);
            setFailed(true);
          }} />}
    </div>
    <h3>{asset.name}</h3>
    <div className="brand-library-actions">
      <a href={url(asset.path)} download={asset.name}>Download {asset.name.endsWith(".svg") ? "SVG" : "PNG"}</a>
      {animated && <button type="button" onClick={() => { setPlaying(true); setRevision(value => value + 1); }}>Play / replay</button>}
    </div>
  </article>;
}

export default function BrandLibrary() {
  const [query, setQuery] = useState("");
  const [surface, setSurface] = useState("dark");
  const [replay, setReplay] = useState(0);
  const search = query.trim().toLowerCase();
  const count = BRAND_ASSET_GROUPS.reduce((total, group) => total + group.assets.length, 0);
  const groups = BRAND_ASSET_GROUPS.map(group => ({
    ...group, assets: group.assets.filter(asset => `${group.title} ${asset.name}`.toLowerCase().includes(search)),
  })).filter(group => group.assets.length);
  return <main className={`brand-library brand-library-${surface}`}>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Jura:wght@300;400;500;600;700&display=swap" />
    <header className="brand-library-header">
      <div><p className="brand-library-eyebrow">ADMIN / DESIGN SYSTEM</p><h1>Brand library</h1></div>
      <nav aria-label="Brand library navigation"><Link to="/login?menu=1">Dashboard selector</Link><AdminDashboardMenu /></nav>
    </header>
    <p className="brand-library-intro">Current logos, animated vector exports, legacy treatments, platform marks, interface elements, colors and typography.
      This catalog is admin-only; downloadable artwork remains available to the application as public static assets.</p>
    <section className="brand-library-native" aria-labelledby="brand-native-heading">
      <div><h2 id="brand-native-heading">01 / Live application branding</h2>
        <p>Native Jura Bold lettering and inline SVG tree. Login and selector use this component, not an image of the lettering.</p>
        <button type="button" onClick={() => setReplay(value => value + 1)}>Replay native animation</button>
      </div>
      <div className="brand-library-live" key={replay}><MindGameBrand /></div>
    </section>
    <section aria-labelledby="brand-colors-heading">
      <h2 id="brand-colors-heading">02 / Palette & typography</h2>
      <div className="brand-library-colors">{BRAND_COLORS.map(([name, color]) =>
        <div key={name}><span style={{ background: color }} /><strong>{name}</strong><code>{color}</code></div>)}</div>
      <div className="brand-library-type"><h3>Jura / 300-700</h3><p>GAMING INTELLIGENCE / mindGAME / 0123456789</p>
        <p>Bold 700 wordmark: -0.055em tracking, 0.018em stroke. Three equal signature bars. Cursor finishes after E.</p>
        <a href={url("brand/mindgame/OFL-Jura.txt")} download>Jura font license</a>
      </div>
    </section>
    <section aria-labelledby="brand-assets-heading">
      <h2 id="brand-assets-heading">03 / Asset catalog <small>{count} files</small></h2>
      <div className="brand-library-tools">
        <label>Filter assets<input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Logo, tree, Twitch, SVG..." /></label>
        <label>Preview background<select value={surface} onChange={event => setSurface(event.target.value)}>
          <option value="dark">Dark</option><option value="light">Light</option><option value="checker">Transparency grid</option>
        </select></label>
      </div>
      {!groups.length && <p role="status">No assets match your filter.</p>}
      {groups.map(group => <section className="brand-library-group" key={group.id} aria-labelledby={`brand-${group.id}`}>
        <h3 id={`brand-${group.id}`}>{group.title} <small>{group.assets.length}</small></h3>
        <div className="brand-library-grid">{group.assets.map(asset => <BrandAsset key={asset.path} asset={asset} />)}</div>
      </section>)}
    </section>
  </main>;
}
